import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const ProgressBar = ({
  isLoading = false,
  color = "#0ea5e9",
  height = 3,
  speed = 50,
  showSpinner = true,
  duration = 1000, // Duration in milliseconds (default 1 second)
  autoTriggerOnRouteChange = true, // Auto trigger on route change
}) => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const location = useLocation();

  // Handle route change auto-trigger
  useEffect(() => {
    if (autoTriggerOnRouteChange) {
      start();
    }
  }, [location.pathname, autoTriggerOnRouteChange]);

  // Handle manual isLoading prop
  useEffect(() => {
    if (!autoTriggerOnRouteChange) {
      if (isLoading) {
        start();
      } else {
        complete();
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isLoading, autoTriggerOnRouteChange]);

  const start = () => {
    // Clear any existing timers
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    setIsVisible(true);
    setProgress(0);

    if (autoTriggerOnRouteChange) {
      // Linear progress for fixed duration
      const totalSteps = duration / speed;
      const progressPerStep = 100 / totalSteps;

      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev + progressPerStep;
          if (newProgress >= 100) {
            clearInterval(intervalRef.current);
            complete();
            return 100;
          }
          return newProgress;
        });
      }, speed);
    } else {
      // Original random progress behavior for manual control
      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev;

          // Slow down as we approach 90%
          const increment =
            prev < 30
              ? Math.random() * 15 + 5
              : prev < 60
              ? Math.random() * 8 + 2
              : Math.random() * 3 + 0.5;

          return Math.min(prev + increment, 90);
        });
      }, speed);
    }
  };

  const complete = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!autoTriggerOnRouteChange) {
      setProgress(100);
    }

    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      setProgress(0);
    }, 200);
  };

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        pointerEvents: "none",
      }}
    >
      {/* Progress Bar */}
      <div
        style={{
          height: `${height}px`,
          backgroundColor: color,
          width: `${progress}%`,
          transition:
            progress === 100
              ? "width 0.2s ease-out, opacity 0.2s ease-out"
              : "width 0.1s ease-out",
          opacity: progress === 100 ? 0 : 1,
          boxShadow: `0 0 10px ${color}, 0 0 5px ${color}`,
          transformOrigin: "left",
        }}
      />

      {/* Spinner */}
      {showSpinner && progress > 0 && progress < 100 && (
        <div
          style={{
            position: "absolute",
            top: `${height + 2}px`,
            right: "15px",
            width: "18px",
            height: "18px",
            border: `2px solid ${color}20`,
            borderTop: `2px solid ${color}`,
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
      )}

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default ProgressBar;
